import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { LotCategoryDialogTourConfig } from './LotCategoryDialogTourConfig';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormColorInputControl } from '@agtech/web/components/Forms/Controls/AgTechFormColorInputControl';
import { AgTechFormTextAreaControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextAreaControl';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { SaleLotCategory, DefaultSaleLotCategory } from '@sales/data/lots/categories/LotCategoryData';
import { useLotCategoriesWriteCategoryAction } from '@sales/data/lots/categories/LotCategoryDataActions';
import { LotDataGetLotCategoriesQuery } from '@sales/data/lots/categories/LotCategoryQueries';
import React from 'react'

export type LotCategoryDialogProps = {
    lotCategoryId?: number,
    onLotCategorySubmitted?: (submittedCategory: SaleLotCategory) => Promise<void>
}

const LotCategoryDialog = (props: LotCategoryDialogProps) => {
    const dialogQuery = useAgTechQuery({
        query: LotDataGetLotCategoriesQuery
    });

    const dialogAction = useLotCategoriesWriteCategoryAction();

    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: {
                    header: props.lotCategoryId ? 'Update Lot Category' : 'Create Lot Category',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={LotCategoryDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: props.lotCategoryId ? 'Update Category' : 'Create Category',
            })}
            dialogData={{
                query: dialogQuery,
                getDialogData: lotCategoryData => {
                    return props.lotCategoryId ? lotCategoryData[props.lotCategoryId] : DefaultSaleLotCategory;
                }
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d
            }}
            dialogBehavior={() => ({
                eventHandlers: {
                    onDialogSubmitted: props.onLotCategorySubmitted
                }
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <div id='lot-category-name'>
                    <AgTechFormRow>
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption='Name'
                            value={category => category.name}
                            focused={true}
                            required={true}
                            width={60}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                name: val
                            })}
                        />
                        <AgTechFormColorInputControl
                            entityType={formContext.entity}
                            caption='Color'
                            value={cat => cat.color}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                color: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='lot-category-description'>
                    <AgTechFormRow>
                        <AgTechFormTextAreaControl
                            entityType={formContext.entity}
                            caption='Description'
                            value={cat => cat.description}
                            rows={5}
                            width={100}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                description: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

LotCategoryDialog.Button = (props: LotCategoryDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text={props.lotCategoryId ? 'Update Category' : 'Create Category'}
        icon={props.lotCategoryId ? 'fas fa-edit' : 'fas fa-plus'}
        {...props}
        action={e => LotCategoryDialog.Open(e, props)}
    />
);

LotCategoryDialog.Open = (e: AgTechClickEventHandler, props?: LotCategoryDialogProps) => e.dialogs.openDialog({
    dialog: () => <LotCategoryDialog lotCategoryId={props?.lotCategoryId} />
});

export default LotCategoryDialog;